/**
 * HandwrittenText — renders a message as if a human wrote it.
 *
 * Per Craig's "controlled imperfection" brief — NOT a fake handwriting
 * font slapped on flat baseline. Three combined layers:
 *
 *   1. Caveat (Google Fonts) — clean modern gel-pen base.
 *   2. Per-character variance — rotation, vertical drift, scale,
 *      letter-spacing — driven by `style_seed` so re-renders stay
 *      consistent across the gallery, wall, downloads.
 *   3. Subtle baseline drift per line so multi-line writing feels
 *      naturally human.
 *
 * Variance budget (per Craig's exact spec):
 *   rotation:        −1.2° → +1.2°
 *   vertical drift:  −1px  → +2px
 *   scale:           0.98  → 1.03
 *   letter spacing:  ±0.4px
 *
 * Seeded RNG: a small mulberry32 implementation keeps the output
 * deterministic — same `style_seed` = same imperfections every time,
 * so a guest's memory snap looks identical on the wall, on a download,
 * and on a share preview.
 */

import React, { useMemo } from 'react';

// ── seeded RNG (mulberry32) ───────────────────────────────────────────
const seeded = (seed) => {
  let s = seed | 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const range = (rng, min, max) => min + (rng() * (max - min));

/**
 * <HandwrittenText
 *    text="What a night ✨"
 *    color="#D4AF5E"
 *    seed={18273}
 *    size={28}
 * />
 */
export const HandwrittenText = ({
  text = '',
  color = '#1A1A1A',
  seed = 1,
  size = 28,
  lineHeight = 1.18,
  align = 'left',
  className = '',
  testId,
}) => {
  // Pre-compute the per-character variance once per (text, seed) tuple.
  const lines = useMemo(() => {
    const rng = seeded(seed || 1);
    return String(text).split('\n').map((line, li) => {
      // Per-line baseline drift — subtle, never more than ±0.8°.
      const lineRotation = range(rng, -0.8, 0.8);
      const lineYDrift = range(rng, -0.5, 0.5);
      const chars = Array.from(line).map((ch) => ({
        ch,
        r: range(rng, -1.2, 1.2),    // rotation deg
        dy: range(rng, -1, 2),       // px
        s: range(rng, 0.98, 1.03),   // scale
        ls: range(rng, -0.4, 0.4),   // letter-spacing px
      }));
      return { line, chars, lineRotation, lineYDrift, idx: li };
    });
  }, [text, seed]);

  return (
    <div
      className={className}
      data-testid={testId}
      style={{
        fontFamily: "'Caveat', 'Patrick Hand', 'Kalam', cursive",
        fontWeight: 500,
        color,
        fontSize: size,
        lineHeight,
        letterSpacing: '0.5px',
        textAlign: align,
        // Subtle "ink on paper" rendering — boosts emotional realism on
        // high-DPI screens without committing to a separate texture layer.
        WebkitFontSmoothing: 'antialiased',
        textRendering: 'geometricPrecision',
      }}
    >
      {lines.map(({ chars, lineRotation, lineYDrift, idx }) => (
        <div
          key={idx}
          style={{
            display: 'block',
            transform: `rotate(${lineRotation}deg) translateY(${lineYDrift}px)`,
            transformOrigin: align === 'center' ? 'center' : 'left',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {chars.map(({ ch, r, dy, s, ls }, ci) => (
            <span
              key={ci}
              style={{
                display: 'inline-block',
                transform: `translateY(${dy}px) rotate(${r}deg) scale(${s})`,
                marginRight: `${ls}px`,
                // Tiny opacity wobble simulates pen-pressure variance.
                opacity: 0.93 + ((Math.abs(r) / 1.2) * 0.07),
              }}
            >
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};

export default HandwrittenText;
